import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/attachment/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/attachment/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/attachment/FETCH_FAILURE'

const CREATE_REQUEST = 'realhub/attachment/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/attachment/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/attachment/CREATE_FAILURE'

const UPDATE_REQUEST = 'realhub/attachment/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/attachment/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/attachment/UPDATE_FAILURE'

const DELETE_REQUEST = 'realhub/attachment/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/attachment/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/attachment/DELETE_FAILURE'

const SELECT_ATTACHMENT = 'realhub/attachment/SELECT_ATTACHMENT'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForEntities: [],
  loading: false,
  selected: {},
  selectedId: null,
  updating: false,
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedForEntities: payload.loadedForEntities,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

export function selectAttachmentId(attachmentId){
  return (dispatch, getState) => {
    const state = getState()
    const { attachments } = state.entities

    const selectedAttachment = attachments[attachmentId] || {}

    dispatch({
      type: SELECT_ATTACHMENT,
      selectedId: attachmentId,
      selected: selectedAttachment,
    })
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function hydrateAttachment(attachment){
  const normalizedJson = normalize(attachment, Schemas.ATTACHMENT)

  return (dispatch) => {
    dispatch(updateEntities(normalizedJson))
  }
}

export function createAttachment(attachment, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      attachment,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/admin/attachments.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ATTACHMENT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateAttachment(attachment, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      attachment,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/admin/attachments/${attachment.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ATTACHMENT)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function loadEntityAttachments(entityType, entityId, options){
  let url
  switch (entityType){
    case 'Brand':
      url = `/admin/brands/${entityId}/attachments.json`
      break
    default:
      url = ''
      break
  }

  const entityKey = `${entityType}${entityId}`

  return (dispatch, getState) => {
    // Set Loading
    const loadedForEntities = [...getState().attachments.loadedForEntities]

    if (!loadedForEntities.includes(entityKey)){
      loadedForEntities.push(entityKey)
    }
    dispatch(fetchRequest({ loadedForEntities }))

    const promise = api(url, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ATTACHMENT_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function deleteAttachment(attachment, callback){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/admin/attachments/${attachment.id}.json`, {}, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }
        const normalizedJson = normalize(json, Schemas.ATTACHMENT)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })
    return promise
  }
}

export function generateAttachmentUrl(attachmentId, options){
  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/admin/attachments/${attachmentId}/generate_attachment_url.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ATTACHMENT)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function createTaskListItem(attachment, comment, options){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      attachment,
      comment_body: comment.body,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/admin/attachments/${attachment.id}/create_task_list_item.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.TASK_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function publishMediaFile(attachment, integrable_id, integrable_type, body, options){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      body,
      integrable_id,
      integrable_type,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api(`/admin/attachments/${attachment.id}/publish_media_file.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ATTACHMENT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedForEntities: action.loadedForEntities || state.loadedForEntities,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    case SELECT_ATTACHMENT:
      return {
        ...state,
        selectedId: action.selectedId,
        selected: action.selected,
      }
    default:
      return state
  }
}
